/**
 * AP-18 7.5 — was ein **Fachmodul** in der Leiste des Assistenten selbst zeichnet.
 *
 * Die Leiste hängt im Rahmen der Anwendung und kennt kein Feature. Ein Feature meldet hier an, was nur
 * es zeichnen kann — und die Leiste ruft es über die Art auf, ohne es zu importieren (Modulgrenzen
 * AP-21-B):
 *
 * - **Beilagen** unter einer Antwort (`AssistentBeilage` des Servers): der Vorschlags-Lauf einer
 *   Kalkulation mit seiner Übernahme, ein Knopf „Starten".
 * - **Verweise mit eigener Darstellung**: Ein Ziel wie `fundstelle:<…>` öffnet einen Dialog statt
 *   einer Seite. Alle übrigen Verweise löst `Antworttext` selbst in Pfade auf.
 * - **Änderungen**: Was in der Leiste übernommen wurde, hat den geöffneten Datensatz geändert — die
 *   Seite lädt neu, wenn sie sich dafür angemeldet hat.
 *
 * Angemeldet wird beim Laden einer Datei des Features (Vorbild `features/calculations/assistent-beilagen.tsx`),
 * und die lädt `App.tsx` beim Start — der Menüpunkt „Assistent" zeigt ein Gespräch, ohne dass die Seite des
 * Features je geöffnet war.
 * Eine Art, die niemand angemeldet hat, wird nicht gezeichnet.
 */
import { useEffect, type ComponentType, type ReactNode } from 'react'

export interface AssistentBeilage {
  /** `<modul>.<was>`: `calculation.run`, `calculation.action` */
  art: string
  daten: Record<string, unknown>
}

export interface BeilageProps {
  daten: Record<string, unknown>
}

export interface VerweisProps {
  /** was nach `art:` steht */
  ziel: string
  children: ReactNode
}

const beilagen = new Map<string, ComponentType<BeilageProps>>()
const verweise = new Map<string, ComponentType<VerweisProps>>()

export function registriereBeilage(art: string, Komponente: ComponentType<BeilageProps>): void {
  beilagen.set(art, Komponente)
}

export function beilageFuer(art: string): ComponentType<BeilageProps> | undefined {
  return beilagen.get(art)
}

export function registriereVerweis(art: string, Komponente: ComponentType<VerweisProps>): void {
  verweise.set(art, Komponente)
}

export function verweisKomponente(art: string): ComponentType<VerweisProps> | undefined {
  return verweise.get(art)
}

// ── Änderungen ──────────────────────────────────────────────────────────────

type Hoerer = (id: string) => void
const hoerer = new Map<string, Set<Hoerer>>()

/** In der Leiste wurde etwas übernommen, das diesen Datensatz ändert (`art` wie bei `useAssistentDatensatz`). */
export function meldeAenderung(art: string, id: string): void {
  for (const h of hoerer.get(art) ?? []) h(id)
}

/** Die Seite eines Datensatzes lädt neu, wenn die Leiste ihn geändert hat. */
export function useAenderung(art: string, id: string | null | undefined, neuLaden: () => void): void {
  useEffect(() => {
    if (!id) return
    const h: Hoerer = (welche) => {
      if (welche === id) neuLaden()
    }
    const menge = hoerer.get(art) ?? new Set<Hoerer>()
    hoerer.set(art, menge)
    menge.add(h)
    return () => {
      menge.delete(h)
    }
  }, [art, id, neuLaden])
}
